import {html, svg, type HTMLTemplateResult} from "lit";

import {fitLabel, indexIn, mod, type PickerSkinInput} from "./picker-skin-input";

const ROW = 40;
/** Rows above and below the middle one inside the window of five. */
const HALF_WINDOW = 2;
/** At least this many rows pass before the reel stops. */
const MIN_ROWS = 15;

/**
 * The reel's position in rows — the pool index (fractional) in the middle of the window, counting on past the
 * end (the reel repeats the names in a circle): at `progress = 0` where the draw before left it (`from`; 0
 * before the first draw), at `progress = 1` exactly on `to`, at least `MIN_ROWS` rows and whole rounds later.
 * Extra slowing on top of the tween's ease-out, so it clicks into place.
 */
export function reelOffset(n: number, from: number | null, to: number, progress: number): number {
    const start = from ?? 0;
    const rounds = Math.max(1, Math.ceil(MIN_ROWS / n));
    const end = start + rounds * n + mod(to - start, n);
    const slowed = 1 - (1 - progress) ** 2;
    return start + (end - start) * slowed;
}

/** Slotmaschine: a reel of the names behind a window of five rows; it stops on the result. */
export function renderSlotSkin({pool, current, previous, progress, phase}: PickerSkinInput): HTMLTemplateResult {
    const n = pool.length;
    const to = indexIn(pool, current);
    const from = indexIn(pool, previous);
    const position = to === null ? 0 : reelOffset(n, phase === 'shown' ? to : from, to, phase === 'shown' ? 1 : progress);
    const base = Math.floor(position);
    const shift = position - base;
    const rows = [];
    for (let j = -HALF_WINDOW - 1; j <= HALF_WINDOW + 1; j++) {
        const entry = pool[mod(base + j, n)];
        const y = 100 + (j - shift) * ROW;
        // Only a shown result is marked — before the first draw the reel just rests on the first name.
        const hit = j === 0 && phase === 'shown';
        rows.push(svg`<text class=${hit ? 'picker-slot__name picker-slot__name--hit' : 'picker-slot__name'}
                            x="100" y=${y.toFixed(2)}>${fitLabel(entry.name, 18)}</text>`);
    }

    return html`
        <svg class="picker-slot" viewBox="0 0 200 200" aria-hidden="true">
            ${svg`
                <rect class="picker-slot__reel" x="8" y="0" width="184" height="200" rx="10"></rect>
                ${rows}
                <rect class="picker-slot__line" x="8" y=${100 - ROW / 2} width="184" height=${ROW}></rect>
                <polygon class="picker-slot__marker" points="0,88 12,100 0,112"></polygon>
                <polygon class="picker-slot__marker" points="200,88 188,100 200,112"></polygon>
            `}
        </svg>
    `;
}
